import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import VoiceDictationInput from '../components/VoiceDictationInput';
import SignatureInput from '../components/SignatureInput';
import { generateNursesNotesPDF, downloadPDF, printPDF } from '../utils/pdfExport';
import { cachePdfPatients } from '../utils/fillable';
import { 
  FileText, Save, ArrowLeft, Download, Printer, User, Heart, 
  Activity, Stethoscope, Brain, Utensils, AlertTriangle, ClipboardList
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NursesNotesForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patientId');
  const formId = searchParams.get('formId');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState([]);
  const [activeSection, setActiveSection] = useState(0);
  const [formData, setFormData] = useState({
    patientId: patientId || '',
    patientName: '',
    visitDate: new Date().toISOString().split('T')[0],
    medicalRecordNumber: '',
    allergies: '',
    medicationChangeSinceLastVisit: false,
    medicationChangeDetails: '',
    isHomebound: true,
    homeboundReason: '',
    // Vital Signs
    temperature: '', pulse: '', respiration: '', weight: '', bloodPressure: '', bloodSugar: '', oxygenSaturation: '',
    universalPrecautionsMaintained: true,
    // Respiratory
    respiratoryNoDeficit: true, hasRalesRhonchi: false, hasSOB: false, hasCough: false, hasSputum: false,
    oxygenAt: '', respiratoryOther: '',
    // Skin
    edemaLocation: '', edemaGrade: '', edemaPitting: false,
    skinNoDeficit: true, skinCoolClammy: false, skinWarmDry: true, turgorAdequate: true,
    wound1Location: '', wound1Length: '', wound1Width: '', wound1Depth: '',
    wound1DrainageAmt: '', wound1DrainageColor: '', wound1DrainageOdor: '',
    // GU
    guNoDeficit: true, hasDistention: false, hasRetention: false, hasBurning: false,
    hasFrequency: false, hasFoleyCath: false, hasIncontinence: false, catheterSize: '',
    // CV
    cvNoDeficit: true, hasChestPain: false, hasDizziness: false, hasEdema: false, hasArrhythmia: false, cvComments: '',
    // MS
    msNoDeficit: true, hasWeakness: false, hasBalanceGaitAbnormal: false, hasLimitedMobility: false,
    hasPain: false, isBedbound: false, isChairbound: false, hasContracture: false, hasParalysis: false,
    usesAssistiveDevice: false, fallPrecautionsMaintained: true,
    // Neuro
    neuroNoDeficit: true, orientedToPerson: true, orientedToPlace: true, orientedToTime: true,
    hasSeizures: false, hearingImpaired: false, speechImpaired: false, visuallyImpaired: false,
    // Digestive
    digestiveNoDeficit: true, lastBM: '', hasNauseaVomiting: false, hasDiarrhea: false,
    hasConstipation: false, hasTubeFeeding: false, dietType: '', mealsAdequate: true,
    // Pain
    painFrequency: 0, painPrimarySite: '', painIntensity: 0, painManagement: '', painTeaching: '', painGoal: '', progressTowardPainGoal: '',
    // Other
    icd10Codes: '', supervisionType: '', aidePresent: false, aideFollowingCarePlan: true, aideCourteous: true,
    aideReportsChanges: true, patientSatisfied: true, carePlanChanged: false, additionalInstructionGiven: false,
    goalsPlans: '', progressBarriers: '', progressTowardGoals: '', teachingToolsUsed: '',
    patientVerbalized: true, conferenceWith: '', conferenceRegarding: '', caseManagementNotes: '',
    patientSignature: { typed: '', audioURL: null, canvasDataURL: null }, 
    nurseSignature: { typed: '', audioURL: null, canvasDataURL: null }
  });

  const sections = [
    { name: 'Observation', icon: User },
    { name: 'Vital Signs', icon: Activity },
    { name: 'Systems Review', icon: Stethoscope },
    { name: 'Neuro/Digestive', icon: Brain },
    { name: 'Pain', icon: AlertTriangle },
    { name: 'Goals & Notes', icon: ClipboardList }
  ];

  useEffect(() => {
    fetchPatients();
    if (formId) fetchForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  const fetchPatients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/patients`, { headers: { 'Authorization': `Bearer ${token}` }});
      const data = await response.json();
      if (data.success) {
        setPatients(data.patients); cachePdfPatients(data.patients);
        if (patientId) {
          const patient = data.patients.find(p => p.id === patientId);
          if (patient) setFormData(prev => ({ ...prev, patientName: `${patient.firstName} ${patient.lastName}` }));
        }
      }
    } catch (error) { console.error('Error:', error); }
  };

  const fetchForm = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/forms/nurses-notes/${formId}`, { headers: { 'Authorization': `Bearer ${token}` }});
      const data = await response.json();
      if (data.success) setFormData(data.form);
    } catch (error) { toast.error('Failed to load form'); }
    finally { setLoading(false); }
  };

  const handlePatientChange = (e) => {
    const patient = patients.find(p => p.id === e.target.value);
    if (patient) setFormData(prev => ({ ...prev, patientId: e.target.value, patientName: `${patient.firstName} ${patient.lastName}` }));
  };

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!formData.patientId) { toast.error('Please select a patient'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = formId ? `${API_URL}/api/forms/nurses-notes/${formId}` : `${API_URL}/api/forms/nurses-notes`;
      const response = await fetch(url, {
        method: formId ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.success) {
        toast.success(formId ? 'Form updated' : 'Form created');
        if (!formId && data.form) navigate(`/dashboard/forms/nurses-notes?formId=${data.form.id}`);
      } else {
        toast.error('Could not save the note. Please check the form and try again.');
      }
    } catch (error) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const renderSection = () => {
    switch(activeSection) {
      case 0: // Observation
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Patient *</Label>
                <select value={formData.patientId} onChange={handlePatientChange}
                  className="w-full mt-1 px-3 py-2 border rounded-md focus:ring-2 focus:ring-[#f39c12]">
                  <option value="">Select patient</option>
                  {patients.map(p => <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>)}
                </select>
              </div>
              <div>
                <Label>Visit Date</Label>
                <Input type="date" value={formData.visitDate} onChange={(e) => handleChange('visitDate', e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Medical Record #</Label>
                <Input value={formData.medicalRecordNumber} onChange={(e) => handleChange('medicalRecordNumber', e.target.value)} className="mt-1" />
              </div>
            </div>
            <div>
              <Label>Allergies</Label>
              <VoiceDictationInput 
                value={formData.allergies} 
                onChange={(e) => handleChange('allergies', e.target.value)}
                rows={2} 
                placeholder="List all known allergies..." 
              />
            </div>
            <div className="flex items-center gap-4">
              <Label className="flex items-center gap-2">
                <Checkbox checked={formData.medicationChangeSinceLastVisit} onCheckedChange={(c) => handleChange('medicationChangeSinceLastVisit', c)} />
                Medication change since last visit?
              </Label>
            </div>
            {formData.medicationChangeSinceLastVisit && (
              <div>
                <Label>Specify Changes</Label>
                <Input value={formData.medicationChangeDetails} onChange={(e) => handleChange('medicationChangeDetails', e.target.value)} className="mt-1" />
              </div>
            )}
            <div className="flex items-center gap-4">
              <Label className="flex items-center gap-2">
                <Checkbox checked={formData.isHomebound} onCheckedChange={(c) => handleChange('isHomebound', c)} />
                Patient is Homebound
              </Label>
            </div>
            {formData.isHomebound && (
              <div>
                <Label>Homebound Reason</Label>
                <Input value={formData.homeboundReason} onChange={(e) => handleChange('homeboundReason', e.target.value)} className="mt-1" placeholder="Reason for homebound status" />
              </div>
            )}
          </div>
        );

      case 1: // Vital Signs
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { field: 'temperature', label: 'Temp °F' },
                { field: 'pulse', label: 'Pulse' },
                { field: 'respiration', label: 'Resp' },
                { field: 'weight', label: 'Weight (lbs)' },
                { field: 'bloodPressure', label: 'BP' },
                { field: 'bloodSugar', label: 'Blood Sugar' },
                { field: 'oxygenSaturation', label: 'O2 Sat %' }
              ].map(({ field, label }) => (
                <div key={field}>
                  <Label>{label}</Label>
                  <Input value={formData[field]} onChange={(e) => handleChange(field, e.target.value)} className="mt-1" />
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 p-3 bg-green-50 rounded">
              <Checkbox checked={formData.universalPrecautionsMaintained} onCheckedChange={(c) => handleChange('universalPrecautionsMaintained', c)} />
              <Label>Universal Precautions Maintained</Label>
            </div>
          </div>
        );

      case 2: // Systems Review
        return (
          <div className="space-y-6">
            {/* Respiratory */}
            <div className="border p-4 rounded-lg">
              <h3 className="font-medium mb-3 text-blue-700">RESPIRATORY</h3>
              <div className="flex flex-wrap gap-3">
                {[
                  { field: 'respiratoryNoDeficit', label: 'No Deficit' },
                  { field: 'hasRalesRhonchi', label: 'Rales/Rhonchi' },
                  { field: 'hasSOB', label: 'SOB' },
                  { field: 'hasCough', label: 'Cough' },
                  { field: 'hasSputum', label: 'Sputum' }
                ].map(({ field, label }) => (
                  <Label key={field} className="flex items-center gap-1 p-2 bg-gray-50 rounded">
                    <Checkbox checked={formData[field]} onCheckedChange={(c) => handleChange(field, c)} />
                    {label}
                  </Label>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div><Label>O2 at L/min</Label><Input value={formData.oxygenAt} onChange={(e) => handleChange('oxygenAt', e.target.value)} className="mt-1" /></div>
                <div><Label>Other</Label><Input value={formData.respiratoryOther} onChange={(e) => handleChange('respiratoryOther', e.target.value)} className="mt-1" /></div>
              </div>
            </div>

            {/* Skin */}
            <div className="border p-4 rounded-lg">
              <h3 className="font-medium mb-3 text-purple-700">SKIN</h3>
              <div className="flex flex-wrap gap-3 mb-3">
                {[
                  { field: 'skinNoDeficit', label: 'No Deficit' },
                  { field: 'skinWarmDry', label: 'Warm/Dry' },
                  { field: 'skinCoolClammy', label: 'Cool/Clammy' },
                  { field: 'turgorAdequate', label: 'Turgor Adequate' }
                ].map(({ field, label }) => (
                  <Label key={field} className="flex items-center gap-1 p-2 bg-gray-50 rounded">
                    <Checkbox checked={formData[field]} onCheckedChange={(c) => handleChange(field, c)} />
                    {label}
                  </Label>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Edema Location</Label><Input value={formData.edemaLocation} onChange={(e) => handleChange('edemaLocation', e.target.value)} className="mt-1" /></div>
                <div><Label>Grade (1-4+)</Label><Input value={formData.edemaGrade} onChange={(e) => handleChange('edemaGrade', e.target.value)} className="mt-1" /></div>
              </div>
              <h4 className="font-medium mt-4 mb-2">Wound 1</h4>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                <div><Label className="text-xs">Location</Label><Input value={formData.wound1Location} onChange={(e) => handleChange('wound1Location', e.target.value)} className="mt-1" /></div>
                <div><Label className="text-xs">L (cm)</Label><Input value={formData.wound1Length} onChange={(e) => handleChange('wound1Length', e.target.value)} className="mt-1" /></div>
                <div><Label className="text-xs">W (cm)</Label><Input value={formData.wound1Width} onChange={(e) => handleChange('wound1Width', e.target.value)} className="mt-1" /></div>
                <div><Label className="text-xs">D (cm)</Label><Input value={formData.wound1Depth} onChange={(e) => handleChange('wound1Depth', e.target.value)} className="mt-1" /></div>
                <div><Label className="text-xs">Drainage</Label><Input value={formData.wound1DrainageAmt} onChange={(e) => handleChange('wound1DrainageAmt', e.target.value)} className="mt-1" /></div>
                <div><Label className="text-xs">Color</Label><Input value={formData.wound1DrainageColor} onChange={(e) => handleChange('wound1DrainageColor', e.target.value)} className="mt-1" /></div>
              </div>
            </div>

            {/* CV */}
            <div className="border p-4 rounded-lg">
              <h3 className="font-medium mb-3 text-red-700">CARDIOVASCULAR</h3>
              <div className="flex flex-wrap gap-3">
                {[
                  { field: 'cvNoDeficit', label: 'No Deficit' },
                  { field: 'hasChestPain', label: 'Chest Pain' },
                  { field: 'hasDizziness', label: 'Dizziness' },
                  { field: 'hasEdema', label: 'Edema' },
                  { field: 'hasArrhythmia', label: 'Arrhythmia' }
                ].map(({ field, label }) => (
                  <Label key={field} className="flex items-center gap-1 p-2 bg-gray-50 rounded">
                    <Checkbox checked={formData[field]} onCheckedChange={(c) => handleChange(field, c)} />
                    {label}
                  </Label>
                ))}
              </div>
              <div className="mt-3"><Label>Comments</Label><Input value={formData.cvComments} onChange={(e) => handleChange('cvComments', e.target.value)} className="mt-1" /></div>
            </div>

            {/* MS */}
            <div className="border p-4 rounded-lg">
              <h3 className="font-medium mb-3 text-orange-700">MUSCULOSKELETAL</h3>
              <div className="flex flex-wrap gap-3">
                {[
                  { field: 'msNoDeficit', label: 'No Deficit' },
                  { field: 'hasWeakness', label: 'Weakness' },
                  { field: 'hasBalanceGaitAbnormal', label: 'Balance/Gait Abnormal' },
                  { field: 'hasLimitedMobility', label: 'Limited Mobility' },
                  { field: 'hasPain', label: 'Pain' },
                  { field: 'isBedbound', label: 'Bedbound' },
                  { field: 'isChairbound', label: 'Chairbound' },
                  { field: 'usesAssistiveDevice', label: 'Uses Assistive Device' }
                ].map(({ field, label }) => (
                  <Label key={field} className="flex items-center gap-1 p-2 bg-gray-50 rounded">
                    <Checkbox checked={formData[field]} onCheckedChange={(c) => handleChange(field, c)} />
                    {label}
                  </Label>
                ))}
              </div>
              <div className="mt-3 p-2 bg-yellow-50 rounded">
                <Label className="flex items-center gap-2">
                  <Checkbox checked={formData.fallPrecautionsMaintained} onCheckedChange={(c) => handleChange('fallPrecautionsMaintained', c)} />
                  Fall Precautions Maintained
                </Label>
              </div>
            </div>

            {/* GU */}
            <div className="border p-4 rounded-lg">
              <h3 className="font-medium mb-3 text-teal-700">GENITOURINARY</h3>
              <div className="flex flex-wrap gap-3">
                {[
                  { field: 'guNoDeficit', label: 'No Deficit' },
                  { field: 'hasDistention', label: 'Distention' },
                  { field: 'hasRetention', label: 'Retention' },
                  { field: 'hasBurning', label: 'Burning' },
                  { field: 'hasFrequency', label: 'Frequency' },
                  { field: 'hasFoleyCath', label: 'Foley Cath' },
                  { field: 'hasIncontinence', label: 'Incontinence' }
                ].map(({ field, label }) => (
                  <Label key={field} className="flex items-center gap-1 p-2 bg-gray-50 rounded">
                    <Checkbox checked={formData[field]} onCheckedChange={(c) => handleChange(field, c)} />
                    {label}
                  </Label>
                ))}
              </div>
            </div>
          </div>
        );

      case 3: // Neuro/Digestive
        return (
          <div className="space-y-6">
            {/* Neurological */}
            <div className="border p-4 rounded-lg">
              <h3 className="font-medium mb-3 text-indigo-700">NEUROLOGICAL</h3>
              <div className="flex flex-wrap gap-3 mb-3">
                {[
                  { field: 'neuroNoDeficit', label: 'No Deficit' },
                  { field: 'orientedToPerson', label: 'Oriented to Person' },
                  { field: 'orientedToPlace', label: 'Oriented to Place' },
                  { field: 'orientedToTime', label: 'Oriented to Time' },
                  { field: 'hasSeizures', label: 'Seizures/Tremors' }
                ].map(({ field, label }) => (
                  <Label key={field} className="flex items-center gap-1 p-2 bg-gray-50 rounded">
                    <Checkbox checked={formData[field]} onCheckedChange={(c) => handleChange(field, c)} />
                    {label}
                  </Label>
                ))}
              </div>
              <h4 className="font-medium mt-3 mb-2 text-sm">Sensory</h4>
              <div className="flex flex-wrap gap-3">
                {[
                  { field: 'hearingImpaired', label: 'Hearing Impaired' },
                  { field: 'speechImpaired', label: 'Speech Impaired' },
                  { field: 'visuallyImpaired', label: 'Visually Impaired' }
                ].map(({ field, label }) => (
                  <Label key={field} className="flex items-center gap-1 p-2 bg-gray-50 rounded">
                    <Checkbox checked={formData[field]} onCheckedChange={(c) => handleChange(field, c)} />
                    {label}
                  </Label>
                ))}
              </div>
            </div>

            {/* Digestive */}
            <div className="border p-4 rounded-lg">
              <h3 className="font-medium mb-3 text-green-700">DIGESTIVE/NUTRITION</h3>
              <div className="flex flex-wrap gap-3 mb-3">
                {[
                  { field: 'digestiveNoDeficit', label: 'No Deficit' },
                  { field: 'hasNauseaVomiting', label: 'N/V' },
                  { field: 'hasDiarrhea', label: 'Diarrhea' },
                  { field: 'hasConstipation', label: 'Constipation' },
                  { field: 'hasTubeFeeding', label: 'Tube Feeding' },
                  { field: 'mealsAdequate', label: 'Meals Adequate' }
                ].map(({ field, label }) => (
                  <Label key={field} className="flex items-center gap-1 p-2 bg-gray-50 rounded">
                    <Checkbox checked={formData[field]} onCheckedChange={(c) => handleChange(field, c)} />
                    {label}
                  </Label>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Last BM</Label><Input value={formData.lastBM} onChange={(e) => handleChange('lastBM', e.target.value)} className="mt-1" /></div>
                <div><Label>Diet Type</Label><Input value={formData.dietType} onChange={(e) => handleChange('dietType', e.target.value)} className="mt-1" /></div>
              </div>
            </div>
          </div>
        );

      case 4: // Pain
        return (
          <div className="space-y-4">
            <div className="bg-red-50 p-4 rounded-lg">
              <h3 className="font-medium text-red-900 mb-3">Pain Assessment</h3>
              <div className="mb-4">
                <Label>Frequency of Pain Interfering with Activity</Label>
                <div className="space-y-2 mt-2">
                  {[
                    { value: 0, label: '0 - None or doesn\'t interfere' },
                    { value: 1, label: '1 - Less than daily' },
                    { value: 2, label: '2 - Daily, not constantly' },
                    { value: 3, label: '3 - All of the time' }
                  ].map(opt => (
                    <Label key={opt.value} className="flex items-center gap-2 p-2 bg-white rounded">
                      <input type="radio" checked={formData.painFrequency === opt.value}
                        onChange={() => handleChange('painFrequency', opt.value)} className="w-4 h-4" />
                      {opt.label}
                    </Label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Primary Pain Site</Label><Input value={formData.painPrimarySite} onChange={(e) => handleChange('painPrimarySite', e.target.value)} className="mt-1" /></div>
                <div>
                  <Label>Pain Intensity (0-10)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="range" min="0" max="10" value={formData.painIntensity}
                      onChange={(e) => handleChange('painIntensity', parseInt(e.target.value))}
                      className="flex-1" />
                    <span className="font-bold text-lg w-8">{formData.painIntensity}</span>
                  </div>
                </div>
              </div>
            </div>
            <div><Label>Current Pain Management & Effectiveness</Label>
              <VoiceDictationInput 
                value={formData.painManagement} 
                onChange={(e) => handleChange('painManagement', e.target.value)}
                rows={2}
                placeholder="Describe pain management..."
              />
            </div>
            <div><Label>Pain Management Teaching</Label>
              <VoiceDictationInput 
                value={formData.painTeaching} 
                onChange={(e) => handleChange('painTeaching', e.target.value)}
                rows={2}
                placeholder="Teaching provided..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Patient Pain Goal</Label><Input value={formData.painGoal} onChange={(e) => handleChange('painGoal', e.target.value)} className="mt-1" /></div>
              <div><Label>Progress Toward Goal</Label><Input value={formData.progressTowardPainGoal} onChange={(e) => handleChange('progressTowardPainGoal', e.target.value)} className="mt-1" /></div>
            </div>
            <div><Label>ICD-10 Diagnosis Codes</Label>
              <VoiceDictationInput 
                value={formData.icd10Codes} 
                onChange={(e) => handleChange('icd10Codes', e.target.value)}
                rows={2}
                placeholder="Enter diagnosis codes..."
              />
            </div>
          </div>
        );

      case 5: // Goals & Notes
        return (
          <div className="space-y-4">
            <div><Label>Goals/Plans</Label>
              <VoiceDictationInput 
                value={formData.goalsPlans} 
                onChange={(e) => handleChange('goalsPlans', e.target.value)}
                rows={3}
                placeholder="Patient goals and care plan..."
              />
            </div>
            <div><Label>Progress/Barriers</Label>
              <VoiceDictationInput 
                value={formData.progressBarriers} 
                onChange={(e) => handleChange('progressBarriers', e.target.value)}
                rows={3}
                placeholder="Progress toward goals and any barriers..."
              />
            </div>
            <div><Label>Interventions & Teaching Tools Used</Label>
              <VoiceDictationInput 
                value={formData.teachingToolsUsed} 
                onChange={(e) => handleChange('teachingToolsUsed', e.target.value)}
                rows={2}
                placeholder="Teaching tools and interventions..."
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={formData.patientVerbalized} onCheckedChange={(c) => handleChange('patientVerbalized', c)} />
              <Label>Patient/Caregiver Verbalized Understanding</Label>
            </div>
            <div><Label>Case Management Notes</Label>
              <VoiceDictationInput 
                value={formData.caseManagementNotes} 
                onChange={(e) => handleChange('caseManagementNotes', e.target.value)}
                rows={3}
                placeholder="Additional case management notes..."
              />
            </div>
            <div className="border-t pt-4">
              <h3 className="font-medium mb-3">Signatures</h3>
              <p className="text-sm text-gray-600 mb-4">Choose your preferred signature method: Type, Voice Recording, or Handwritten</p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <SignatureInput 
                  label="Patient Signature" 
                  value={formData.patientSignature} 
                  onChange={(value) => handleChange('patientSignature', value)} 
                />
                <SignatureInput 
                  label="Nurse Signature" 
                  value={formData.nurseSignature} 
                  onChange={(value) => handleChange('nurseSignature', value)} 
                />
              </div>
            </div>
          </div>
        );

      default: return null;
    }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#f39c12]"></div></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate('/dashboard/documentation')} className="p-2"><ArrowLeft className="w-5 h-5" /></Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-[#f39c12]" />Comprehensive Nurses Notes
                </h1>
                <p className="text-gray-500 text-sm mt-1 flex items-center gap-2">
                  Nursing Visit Record 
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => {
                const doc = generateNursesNotesPDF(formData);
                downloadPDF(doc, `nurses-notes-${formData.patientName || 'form'}.pdf`);
              }} data-testid="export-pdf-btn"><Download className="w-4 h-4 mr-2" />PDF</Button>
              <Button variant="outline" onClick={() => {
                const doc = generateNursesNotesPDF(formData);
                printPDF(doc);
              }} data-testid="print-pdf-btn"><Printer className="w-4 h-4 mr-2" />Print</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#f39c12] hover:bg-[#e67e22] text-white">
                {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" />Save</>}
              </Button>
            </div>
          </div>
        </div>

        {/* Section Navigation */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6 overflow-x-auto">
          <div className="flex gap-2 min-w-max">
            {sections.map((section, i) => {
              const Icon = section.icon;
              return (
                <button key={i} onClick={() => setActiveSection(i)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeSection === i ? 'bg-[#f39c12] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}>
                  <Icon className="w-4 h-4" />{section.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">{renderSection()}</div>

        {/* Navigation */}
        <div className="flex justify-between items-center mb-8">
          <Button variant="outline" onClick={() => setActiveSection(p => Math.max(0, p - 1))} disabled={activeSection === 0}>Previous</Button>
          <span className="text-sm text-gray-500">Section {activeSection + 1} of {sections.length}</span>
          <Button onClick={() => setActiveSection(p => Math.min(sections.length - 1, p + 1))} disabled={activeSection === sections.length - 1}
            className="bg-[#f39c12] hover:bg-[#e67e22] text-white">Next</Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default NursesNotesForm;
